import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { CONFIG } from 'variables/config';
import { MdCheckCircle, MdHelpOutline, MdRefresh, MdSend, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import LeadDebugButton from './LeadDebugButton';

// Onglet « Leads no-match » : leads sans acheteur, encore disponibles, à proposer à Batiweb.
// « Masquer » retire un lead de la liste (il n'est alors plus proposé à Batiweb) ; « Afficher les
// leads masqués » liste ceux-là seulement, avec « Réafficher ». Les cases à cocher permettent d'envoyer
// plusieurs leads d'un coup : ils partent un par un, chacun avec son propre résultat.

interface LeadContact {
    firstname: string | null;
    lastname: string | null;
    email: string | null;
    phone: string | null;
    zipcode: string | null;
    city: string | null;
}

interface HelloArtisanState {
    status: 'sending' | 'not_sellable' | 'pending' | 'sold' | 'unsold' | 'error';
    lastCode?: number;
    lastMessage?: string;
    updatedAt?: string;
}

interface NoMatchLead {
    _id: string;
    createdAt: string;
    contact: LeadContact;
    seller: string | null;
    invalidReason: string | null;
    industry: string | null;
    subIndustry: string | null;
    worktypeId: number | null;
    worktypeLabel: string | null;
    helloArtisan: HelloArtisanState | null;
    hiddenAt: string | null;
    hiddenBy: string | null;
}

type RowResult = { ok: boolean; message: string };

// Résultat d'un essai précédent, affiché pour ne pas renvoyer à l'aveugle.
const PREVIOUS_ATTEMPT_LABELS: Partial<Record<HelloArtisanState['status'], string>> = {
    not_sellable: 'Non vendable',
    unsold: 'Annulé par Batiweb',
    error: 'Erreur',
};

const PERIODS = [7, 14, 30];

export default function NoMatchLeadsTab() {
    const [leads, setLeads] = useState<NoMatchLead[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [days, setDays] = useState(14);
    const [industryFilter, setIndustryFilter] = useState('');
    const [withEmail, setWithEmail] = useState(false);
    const [showHidden, setShowHidden] = useState(false);
    const [busy, setBusy] = useState<{ id: string; action: 'test' | 'send' | 'hide' } | null>(null);
    const [results, setResults] = useState<Record<string, RowResult>>({});
    const [banner, setBanner] = useState('');
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);

    const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

    const fetchLeads = useCallback(async () => {
        try {
            setLoading(true);
            setError('');
            const res = await axios.get(`${CONFIG.apiUrl}/admin/batiweb/no-match-leads`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                params: { days, withEmail: withEmail ? '1' : undefined, hidden: showHidden ? '1' : undefined },
            });
            setLeads(res.data.leads);
            setSelected(new Set());
        } catch (e: any) {
            setError(e.response?.data?.message || 'Impossible de charger les leads no-match.');
        } finally {
            setLoading(false);
        }
    }, [days, withEmail, showHidden]);

    useEffect(() => {
        fetchLeads();
    }, [fetchLeads]);

    const industries = useMemo(
        () => Array.from(new Set(leads.map((l) => l.industry).filter(Boolean) as string[])).sort(),
        [leads]
    );
    const visibleLeads = useMemo(
        () => (industryFilter ? leads.filter((l) => l.industry === industryFilter) : leads),
        [leads, industryFilter]
    );
    const isSelectable = (lead: NoMatchLead) => lead.worktypeId != null && !lead.hiddenAt;
    const selectableLeads = useMemo(() => visibleLeads.filter(isSelectable), [visibleLeads]);
    // Seuls les leads encore visibles comptent (un filtre industrie peut en masquer des cochés).
    const selectedLeads = useMemo(() => selectableLeads.filter((l) => selected.has(l._id)), [selectableLeads, selected]);
    const allSelected = selectableLeads.length > 0 && selectedLeads.length === selectableLeads.length;

    const toggleSelected = (id: string) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };
    const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectableLeads.map((l) => l._id)));

    const handleTest = async (lead: NoMatchLead) => {
        try {
            setBusy({ id: lead._id, action: 'test' });
            const res = await axios.post(`${CONFIG.apiUrl}/admin/batiweb/leads/${lead._id}/check-sellable`, {}, { headers });
            setResults((prev) => ({ ...prev, [lead._id]: { ok: res.data.sellable, message: res.data.message } }));
        } catch (e: any) {
            setResults((prev) => ({ ...prev, [lead._id]: { ok: false, message: e.response?.data?.message || 'Erreur lors du test.' } }));
        } finally {
            setBusy(null);
        }
    };

    const leadName = (lead: NoMatchLead) => [lead.contact.firstname, lead.contact.lastname].filter(Boolean).join(' ') || lead._id;

    // Envoie un lead et met à jour la liste. Renvoie true s'il a quitté la Bourse (importé ou import incertain).
    const sendLead = async (lead: NoMatchLead): Promise<{ left: boolean; conflict?: boolean; message: string }> => {
        try {
            const res = await axios.post(`${CONFIG.apiUrl}/admin/batiweb/leads/${lead._id}/send`, {}, { headers });
            if (res.data.status === 'pending' || res.data.status === 'sending') {
                // Importé (→ onglet « En cours »), ou import incertain après une erreur réseau : reste hors Bourse, le cron tranchera.
                setLeads((prev) => prev.filter((l) => l._id !== lead._id));
                return { left: true, message: res.data.status === 'pending' ? "importé chez Batiweb — visible dans l'onglet « En cours »." : res.data.message };
            }
            if (res.data.waterfall?.ok) {
                // Refusé par Batiweb puis attribué à une offre locked : le lead n'est plus no-match.
                setLeads((prev) => prev.filter((l) => l._id !== lead._id));
                return { left: true, message: `refusé par Batiweb (${res.data.message}), attribué au waterfall : ${res.data.waterfall.buyerEmail}.` };
            }
            setResults((prev) => ({ ...prev, [lead._id]: { ok: false, message: res.data.message } }));
            setLeads((prev) => prev.map((l) => (l._id === lead._id ? { ...l, helloArtisan: { status: res.data.status, lastMessage: res.data.message } } : l)));
            return { left: false, message: res.data.message };
        } catch (e: any) {
            const message = e.response?.data?.message || "Erreur lors de l'envoi.";
            setResults((prev) => ({ ...prev, [lead._id]: { ok: false, message } }));
            return { left: false, conflict: e.response?.status === 409, message };
        }
    };

    const handleSend = async (lead: NoMatchLead) => {
        const name = leadName(lead);
        if (!window.confirm(`Envoyer ${name} à Batiweb sur le métier « ${lead.worktypeLabel} » ?\n\nS'il est vendable, il sera importé chez eux et retiré de la Bourse du Lead.`)) {
            return;
        }
        setBusy({ id: lead._id, action: 'send' });
        setBanner('');
        const { left, conflict, message } = await sendLead(lead);
        if (left) setBanner(`${name} : ${message}`);
        setBusy(null);
        if (conflict) fetchLeads();
    };

    const handleSendSelected = async () => {
        const toSend = selectedLeads;
        if (toSend.length === 0) return;
        if (!window.confirm(`Envoyer ${toSend.length} lead(s) à Batiweb ?\n\nIls partent un par un ; ceux qui sont vendables seront importés chez eux et retirés de la Bourse du Lead.`)) {
            return;
        }
        setBanner('');
        setBulkProgress({ done: 0, total: toSend.length });
        let sent = 0;
        let conflict = false;
        for (const [i, lead] of toSend.entries()) {
            setBusy({ id: lead._id, action: 'send' });
            const res = await sendLead(lead);
            if (res.left) sent += 1;
            if (res.conflict) conflict = true;
            setSelected((prev) => {
                const next = new Set(prev);
                next.delete(lead._id);
                return next;
            });
            setBulkProgress({ done: i + 1, total: toSend.length });
        }
        setBusy(null);
        setBulkProgress(null);
        // Un 409 = état changé côté serveur : on recharge à la fin plutôt qu'au milieu de la boucle.
        if (conflict) fetchLeads();
        const failed = toSend.length - sent;
        setBanner(`${sent}/${toSend.length} lead(s) importé(s) chez Batiweb — visibles dans l'onglet « En cours ».${failed ? ` ${failed} non envoyé(s) : voir le motif sur chaque ligne.` : ''}`);
    };

    // Masquer (ou réafficher) : le lead quitte la liste affichée dans les deux cas.
    const handleToggleHidden = async (lead: NoMatchLead) => {
        try {
            setBusy({ id: lead._id, action: 'hide' });
            setBanner('');
            const url = `${CONFIG.apiUrl}/admin/batiweb/leads/${lead._id}/hide`;
            if (lead.hiddenAt) await axios.delete(url, { headers });
            else await axios.post(url, {}, { headers });
            setLeads((prev) => prev.filter((l) => l._id !== lead._id));
        } catch (e: any) {
            setResults((prev) => ({ ...prev, [lead._id]: { ok: false, message: e.response?.data?.message || 'Erreur lors du masquage.' } }));
        } finally {
            setBusy(null);
        }
    };

    return (
        <>
            <div className="mb-4 flex flex-wrap items-end gap-4">
                <div>
                    <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">Période</label>
                    <select
                        value={days}
                        onChange={(e) => setDays(Number(e.target.value))}
                        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                    >
                        {PERIODS.map((p) => (
                            <option key={p} value={p}>{p} derniers jours</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="mb-1 block text-xs font-semibold uppercase text-gray-500">Industrie</label>
                    <select
                        value={industryFilter}
                        onChange={(e) => setIndustryFilter(e.target.value)}
                        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                    >
                        <option value="">Toutes</option>
                        {industries.map((industry) => (
                            <option key={industry} value={industry}>{industry}</option>
                        ))}
                    </select>
                </div>
                <label className="flex items-center gap-2 pb-2 text-sm text-gray-600 dark:text-gray-300">
                    <input type="checkbox" checked={withEmail} onChange={(e) => setWithEmail(e.target.checked)} />
                    Seulement les leads avec un email
                </label>
                <label className="flex items-center gap-2 pb-2 text-sm text-gray-600 dark:text-gray-300">
                    <input type="checkbox" checked={showHidden} onChange={(e) => setShowHidden(e.target.checked)} />
                    Afficher les leads masqués
                </label>
                <button
                    onClick={fetchLeads}
                    disabled={loading}
                    className="ml-auto flex items-center gap-1 rounded-lg bg-gray-100 px-3 py-2 text-xs font-semibold text-navy-700 hover:bg-gray-200 disabled:opacity-50 dark:bg-navy-700 dark:text-white"
                >
                    <MdRefresh />
                    Actualiser
                </button>
            </div>

            <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
                Leads no-match sans acheteur, encore visibles sur la Bourse du Lead. « Tester » vérifie seulement s'il est vendable
                (is_sellable) ; « Envoyer » fait tout le process : is_sellable, import chez Batiweb, retrait de la Bourse.
            </p>

            {error && <div className="mb-4 rounded-lg bg-red-100 p-3 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">{error}</div>}
            {banner && <div className="mb-4 rounded-lg bg-green-100 p-3 text-sm text-green-700 dark:bg-green-900/30 dark:text-green-400">{banner}</div>}

            {loading ? (
                <div className="py-10 text-center text-gray-400">Chargement...</div>
            ) : visibleLeads.length === 0 ? (
                <div className="py-10 text-center text-gray-400">
                    {showHidden ? 'Aucun lead masqué sur la période.' : 'Aucun lead no-match disponible sur la période.'}
                </div>
            ) : (
                <div className="overflow-x-auto">
                    <div className="mb-2 flex items-center gap-3">
                        <span className="text-xs text-gray-400">{visibleLeads.length} lead(s)</span>
                        {!showHidden && (
                            <button
                                onClick={handleSendSelected}
                                disabled={selectedLeads.length === 0 || !!busy}
                                className="flex items-center gap-1 whitespace-nowrap rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-40"
                            >
                                <MdSend />
                                {bulkProgress
                                    ? `Envoi ${bulkProgress.done}/${bulkProgress.total}...`
                                    : `Envoyer la sélection à Batiweb (${selectedLeads.length})`}
                            </button>
                        )}
                    </div>
                    <table className="w-full text-left text-sm">
                        <thead>
                            <tr className="border-b border-gray-200 dark:border-navy-600">
                                {!showHidden && (
                                    <th className="pb-3 pr-3">
                                        <input
                                            type="checkbox"
                                            checked={allSelected}
                                            onChange={toggleAll}
                                            disabled={selectableLeads.length === 0 || !!busy}
                                            title="Tout sélectionner"
                                        />
                                    </th>
                                )}
                                <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Date</th>
                                <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Lead</th>
                                <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Industrie</th>
                                <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Produit leadrs</th>
                                <th className="pb-3 pr-4 text-xs font-bold uppercase text-gray-500">Métier Batiweb</th>
                                <th className="pb-3 text-xs font-bold uppercase text-gray-500">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visibleLeads.map((lead) => {
                                const result = results[lead._id];
                                const previous = lead.helloArtisan ? PREVIOUS_ATTEMPT_LABELS[lead.helloArtisan.status] : undefined;
                                const canSend = lead.worktypeId != null;
                                const isBusy = busy?.id === lead._id;
                                return (
                                    <tr key={lead._id} className="border-b border-gray-100 align-top dark:border-navy-700">
                                        {!showHidden && (
                                            <td className="py-3 pr-3">
                                                <input
                                                    type="checkbox"
                                                    checked={selected.has(lead._id)}
                                                    onChange={() => toggleSelected(lead._id)}
                                                    disabled={!isSelectable(lead) || !!busy}
                                                />
                                            </td>
                                        )}
                                        <td className="py-3 pr-4 text-xs text-gray-500 whitespace-nowrap">
                                            {new Date(lead.createdAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                                        </td>
                                        <td className="py-3 pr-4">
                                            <div className="flex items-center gap-1.5 font-semibold text-navy-700 dark:text-white">
                                                {[lead.contact.firstname, lead.contact.lastname].filter(Boolean).join(' ') || '—'}
                                                <LeadDebugButton leadId={lead._id} onEdited={fetchLeads} />
                                            </div>
                                            {lead.hiddenAt && (
                                                <div className="text-xs text-gray-400">
                                                    Masqué le {new Date(lead.hiddenAt).toLocaleDateString('fr-FR')}{lead.hiddenBy ? ` par ${lead.hiddenBy}` : ''}
                                                </div>
                                            )}
                                            <div className="text-xs text-gray-500">{lead.contact.email || '—'} · {lead.contact.phone || '—'}</div>
                                            <div className="text-xs text-gray-500">
                                                {lead.contact.zipcode || 'CP ?'}{lead.contact.city ? ` ${lead.contact.city}` : ''}
                                                {lead.seller && <span className="text-gray-400"> · vendeur {lead.seller}</span>}
                                            </div>
                                        </td>
                                        <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">{lead.industry || <span className="text-gray-400">Inconnue</span>}</td>
                                        <td className="py-3 pr-4 text-gray-600 dark:text-gray-300">{lead.subIndustry || <span className="text-gray-400">Inconnu</span>}</td>
                                        <td className="py-3 pr-4">
                                            {canSend && (
                                                <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                                                    {lead.worktypeLabel} ({lead.worktypeId})
                                                </span>
                                            )}
                                            {previous && (
                                                <div className="mt-1 text-xs text-orange-600">
                                                    Dernier essai : {previous}{lead.helloArtisan?.lastMessage ? ` — ${lead.helloArtisan.lastMessage}` : ''}
                                                </div>
                                            )}
                                        </td>
                                        <td className="py-3">
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => handleTest(lead)}
                                                    disabled={!canSend || !!busy || !!lead.hiddenAt}
                                                    className="flex items-center gap-1 whitespace-nowrap rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-semibold text-navy-700 hover:bg-gray-200 disabled:opacity-40 dark:bg-navy-700 dark:text-white"
                                                >
                                                    <MdHelpOutline />
                                                    {isBusy && busy?.action === 'test' ? 'Test...' : 'Tester is_sellable'}
                                                </button>
                                                <button
                                                    onClick={() => handleSend(lead)}
                                                    disabled={!canSend || !!busy || !!lead.hiddenAt}
                                                    className="flex items-center gap-1 whitespace-nowrap rounded-lg bg-brand-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-40"
                                                >
                                                    <MdSend />
                                                    {isBusy && busy?.action === 'send' ? 'Envoi...' : 'Envoyer à Batiweb'}
                                                </button>
                                                <button
                                                    onClick={() => handleToggleHidden(lead)}
                                                    disabled={!!busy}
                                                    title={lead.hiddenAt ? 'Remettre ce lead dans la liste' : 'Retirer ce lead de la liste : il ne sera plus proposé à Batiweb'}
                                                    className="flex items-center gap-1 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-semibold text-gray-500 hover:bg-gray-100 disabled:opacity-40 dark:hover:bg-navy-700"
                                                >
                                                    {lead.hiddenAt ? <MdVisibility /> : <MdVisibilityOff />}
                                                    {isBusy && busy?.action === 'hide' ? '...' : lead.hiddenAt ? 'Réafficher' : 'Masquer'}
                                                </button>
                                            </div>
                                            {result && (
                                                <div className={`mt-2 flex items-center gap-1 text-xs ${result.ok ? 'text-green-600' : 'text-red-600'}`}>
                                                    {result.ok && <MdCheckCircle />}
                                                    {result.message}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );
}
